import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
    Tooltip,
    Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

const safeParseJSON = (value, fallback) => {
    if (!value) {
        return fallback;
    }
    try {
        return JSON.parse(value);
    } catch (error) {
        console.error('Failed to parse JSON bootstrap payload', error);
        return fallback;
    }
};

const markerTypeOptions = [
    { value: 'start', label: 'Start' },
    { value: 'end', label: 'End' },
    { value: 'midpoint', label: 'Midpoint' },
    { value: 'custom', label: 'Custom' },
];

const defaultMetadata = Object.freeze({ real_dx: '', real_dy: '', height: '' });

const buildMarkerDialogState = (overrides = {}) => ({
    open: false,
    mode: 'create',
    markerSetId: null,
    markerId: null,
    label: '',
    type: 'midpoint',
    sequence: 50,
    x_scaled: 0.5,
    y_scaled: 0.5,
    metadata: { ...defaultMetadata },
    definitions: [],
    ...overrides,
});

const toMetadataState = (metadata = {}) => ({
    ...defaultMetadata,
    ...metadata,
    real_dx: metadata.real_dx ?? '',
    real_dy: metadata.real_dy ?? '',
    height: metadata.height ?? '',
});

const sanitizeMetadataPayload = (metadata = {}) => {
    const normalized = { ...metadata };
    ['real_dx', 'real_dy', 'height'].forEach((key) => {
        const value = metadata[key];
        if (value === '' || value === null || typeof value === 'undefined') {
            normalized[key] = null;
            return;
        }
        const numeric = Number(value);
        normalized[key] = Number.isFinite(numeric) ? numeric : null;
    });
    return normalized;
};

const getCsrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const resolveVariant = (label = '') => {
    const parts = label.split('_');
    if (parts.length >= 2) {
        return parts[1];
    }
    return null;
};

const isMarkerSetLocked = (markerSet) => Boolean(markerSet?.metadata?.locked);

/**
 * Marker drawer that keeps Fabric overlays, templates, and backend APIs in sync.
 */
const MarkersManager = ({ bootstrap = {} }) => {
    const baseUrl = bootstrap.baseUrl || bootstrap.baseurl || '';
    const disabled = bootstrap.disabled === 'true';
    const maxSets = Number(bootstrap.maxSets || 5);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [markerSets, setMarkerSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [markerDialog, setMarkerDialog] = useState(buildMarkerDialogState);
    const [placementTarget, setPlacementTarget] = useState(null);
    const [pendingEditRequest, setPendingEditRequest] = useState(null);
    const [pendingSaveCount, setPendingSaveCount] = useState(0);
    const [lastSaveTimestamp, setLastSaveTimestamp] = useState(null);
    const [saveSnackbarState, setSaveSnackbarState] = useState({ open: false, message: '' });
    const [lockSavingState, setLockSavingState] = useState({});
    const [markerUsage, setMarkerUsage] = useState({});
    const helperStorageKey = useMemo(
        () => (baseUrl ? `markers-helper-dismissed:${baseUrl}` : 'markers-helper-dismissed'),
        [baseUrl]
    );
    const [showAutosaveHelper, setShowAutosaveHelper] = useState(() => {
        if (typeof window === 'undefined') {
            return true;
        }
        try {
            return localStorage.getItem(helperStorageKey) !== 'dismissed';
        } catch (storageError) {
            console.warn('Unable to read marker helper preference', storageError);
            return true;
        }
    });

    const canEditMarkerSet = useCallback((markerSetId) => {
        if (disabled || !markerSetId) {
            return false;
        }
        const targetSet = markerSets.find(
            (set) => (set.id ?? '').toString() === (markerSetId ?? '').toString()
        );
        if (!targetSet) {
            return false;
        }
        return !isMarkerSetLocked(targetSet);
    }, [disabled, markerSets]);

    const resetMarkerDialog = useCallback(() => {
        setMarkerDialog(buildMarkerDialogState());
    }, []);

    const handleMarkerDialogClose = useCallback(() => {
        resetMarkerDialog();
    }, [resetMarkerDialog]);

    useEffect(() => {
        const handleUsageUpdate = (event) => {
            setMarkerUsage(event?.detail || {});
        };
        window.addEventListener('markers:usage:update', handleUsageUpdate);
        window.dispatchEvent(new CustomEvent('markers:usage:request'));
        return () => {
            window.removeEventListener('markers:usage:update', handleUsageUpdate);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            setShowAutosaveHelper(localStorage.getItem(helperStorageKey) !== 'dismissed');
        } catch (storageError) {
            console.warn('Unable to sync marker helper preference', storageError);
        }
    }, [helperStorageKey]);

    const handleDismissAutosaveHelper = useCallback(() => {
        setShowAutosaveHelper(false);
        if (typeof window === 'undefined') {
            return;
        }
        try {
            localStorage.setItem(helperStorageKey, 'dismissed');
        } catch (storageError) {
            console.warn('Unable to persist marker helper preference', storageError);
        }
    }, [helperStorageKey]);

    const scrollCanvasIntoView = useCallback(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const canvasElement = document.getElementById('canvas');
        const fallbackAnchor = document.getElementById('topvideo');
        const targetElement = canvasElement || fallbackAnchor;

        if (targetElement) {
            const preferredOffset = 70;
            const rect = targetElement.getBoundingClientRect();
            const absoluteTop = rect.top + window.scrollY - preferredOffset;
            window.scrollTo({ top: Math.max(absoluteTop, 0), behavior: 'smooth' });
        } else if (typeof window.location !== 'undefined') {
            window.location.hash = 'topvideo';
        }
    }, []);

    const colorSchemes = useMemo(
        () => safeParseJSON(bootstrap.colorSchemes, {}),
        [bootstrap.colorSchemes]
    );
    const templates = useMemo(() => safeParseJSON(bootstrap.templates, {}), [bootstrap.templates]);

    const metadataDefaultsForType = useCallback((type) => {
        const template = templates[type] || {};
        const defaults = template.metadata_defaults || {};
        return toMetadataState({ ...defaultMetadata, ...defaults });
    }, [templates]);

    const computeAvailableDefinitions = useCallback((markerSet) => {
        if (!markerSet) {
            return [];
        }
        const template = templates[markerSet.type] || {};
        const requiredMarkers = Array.isArray(template.required_markers) ? template.required_markers : [];
        const optionalMarkers = Array.isArray(template.optional_markers) ? template.optional_markers : [];
        const existingLabels = new Set((markerSet.markers || []).map((marker) => marker.label));

        const buildDefinition = (definition) => {
            if (!definition) {
                return null;
            }
            const baseLabel = definition.label || definition.baseLabel || definition.type || 'marker';
            const repeatable = Boolean(definition.repeatable);

            if (!repeatable && existingLabels.has(baseLabel)) {
                return null;
            }

            let nextLabel = baseLabel;
            if (repeatable) {
                let suffix = 1;
                if (existingLabels.has(nextLabel)) {
                    suffix = 2;
                    while (existingLabels.has(`${baseLabel}_${suffix}`)) {
                        suffix += 1;
                    }
                    nextLabel = `${baseLabel}_${suffix}`;
                }
            }

            return {
                ...definition,
                baseLabel,
                label: nextLabel,
                optionLabel: repeatable && nextLabel !== baseLabel ? `${baseLabel} (${nextLabel})` : nextLabel,
                repeatable,
            };
        };

        return [...requiredMarkers, ...optionalMarkers]
            .map(buildDefinition)
            .filter(Boolean);
    }, [templates]);

    const handleOpenMarkerDialog = useCallback((markerSet) => {
        if (!markerSet || disabled || isMarkerSetLocked(markerSet)) {
            return;
        }

        const definitions = computeAvailableDefinitions(markerSet);
        if (!definitions.length) {
            setError('All template markers for this set have already been created.');
            return;
        }

        const defaults = metadataDefaultsForType(markerSet.type);
        const selectedDefinition = definitions[0];

        setMarkerDialog(buildMarkerDialogState({
            open: true,
            markerSetId: markerSet.id,
            label: selectedDefinition.label || '',
            type: selectedDefinition.type || 'midpoint',
            sequence: selectedDefinition.sequence ?? 50,
            metadata: defaults,
            definitions,
        }));
    }, [computeAvailableDefinitions, disabled, metadataDefaultsForType]);

    const handleEditMarker = useCallback((markerSet, marker) => {
        if (!markerSet || !marker || disabled || isMarkerSetLocked(markerSet)) {
            return;
        }
        setMarkerDialog(buildMarkerDialogState({
            open: true,
            mode: 'edit',
            markerSetId: markerSet.id,
            markerId: marker.id,
            label: marker.label,
            type: marker.type,
            sequence: marker.sequence ?? 0,
            metadata: toMetadataState(marker.metadata || {}),
        }));
    }, [disabled]);

    const handleRequestPlacement = useCallback((markerSetId, markerId, label) => {
        if (!markerSetId || !markerId || !canEditMarkerSet(markerSetId)) {
            return;
        }

        setPlacementTarget({ markerSetId, markerId, label });
        window.dispatchEvent(
            new CustomEvent('markers:place', {
                detail: {
                    markerSetId,
                    markerId,
                },
            })
        );
        setDrawerOpen(false);
    }, [canEditMarkerSet, setDrawerOpen]);

    const handleMoveMarker = useCallback((markerSetId, markerId) => {
        if (!markerSetId || !markerId || !canEditMarkerSet(markerSetId)) {
            return;
        }

        handleReveal(markerSetId, markerId, {
            closeDrawer: true,
            scrollToCanvas: true,
            select: true,
            flash: { pulseCount: 2, durationMs: 260 },
        });
    }, [canEditMarkerSet, handleReveal]);
    const defaultColorKey = useMemo(() => Object.keys(colorSchemes)[0] || 'ember_forge', [colorSchemes]);
    const defaultType = useMemo(() => Object.keys(templates)[0] || 'calibration_square', [templates]);

    const [createForm, setCreateForm] = useState({
        name: '',
        color_key: defaultColorKey,
        type: defaultType,
        legend: '',
    });

    useEffect(() => {
        setCreateForm((prev) => ({
            ...prev,
            color_key: defaultColorKey,
            type: defaultType,
        }));
    }, [defaultColorKey, defaultType]);

    const csrfToken = useMemo(() => getCsrfToken(), []);

    const normalizeMarkers = useCallback((rawSets = []) => {
        return rawSets.map((set) => {
            const markersPayload = Array.isArray(set?.markers?.data)
                ? set.markers.data
                : (Array.isArray(set?.markers) ? set.markers : []);
            return {
                ...set,
                markers: markersPayload,
            };
        });
    }, []);

    const fetchSets = useCallback(async () => {
        if (!baseUrl) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(baseUrl, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (!response.ok) {
                throw new Error(`Failed to load marker sets (${response.status})`);
            }
            const payload = await response.json();
            const nextSets = normalizeMarkers(payload?.data || payload);
            setMarkerSets(nextSets);
        } catch (requestError) {
            console.error(requestError);
            setError('Unable to load markers. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [baseUrl, normalizeMarkers]);

    useEffect(() => {
        fetchSets();
    }, [fetchSets]);

    const updateCanvasOverlay = useCallback(
        (sets) => {
            if (typeof window === 'undefined') {
                return;
            }

            const payload = (sets || []).map((set) => {
                const locked = isMarkerSetLocked(set);
                return {
                    id: set.id,
                    colorKey: set.color_key,
                    type: set.type,
                    name: set.name,
                    locked,
                    markers: (set.markers || []).map((marker) => ({
                        id: marker.id,
                        markerSetId: set.id,
                        markerSetName: set.name,
                        label: marker.label,
                        type: marker.type,
                        coords: marker.coords || { x_scaled: 0, y_scaled: 0 },
                        uiColor: resolveMarkerColors(colorSchemes, set.color_key, marker, templates[set.type]),
                        locked,
                    })),
                };
            });

            window.dispatchEvent(
                new CustomEvent('markers:render', {
                    detail: {
                                                markerSets: payload,
                                                editable: !disabled,
                    },
                })
            );
                }, [colorSchemes, disabled, templates]
    );

    useEffect(() => {
        updateCanvasOverlay(markerSets);
    }, [markerSets, updateCanvasOverlay]);

    useEffect(() => {
        const handlePlacementComplete = (event) => {
            const markerId = event?.detail?.markerId ?? null;
            setPlacementTarget((current) => {
                if (!current) {
                    return current;
                }
                if (!markerId || current.markerId === markerId) {
                    return null;
                }
                return current;
            });
        };

        window.addEventListener('markers:placement:complete', handlePlacementComplete);
        return () => {
            window.removeEventListener('markers:placement:complete', handlePlacementComplete);
        };
    }, []);

    const cancelPlacement = useCallback(() => {
        setPlacementTarget(null);
        window.dispatchEvent(new CustomEvent('markers:place:cancel'));
    }, []);

    useEffect(() => {
        if (!placementTarget) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                cancelPlacement();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [placementTarget, cancelPlacement]);

    useEffect(() => {
        if (!placementTarget) {
            return;
        }
        if (!canEditMarkerSet(placementTarget.markerSetId)) {
            cancelPlacement();
        }
    }, [placementTarget, canEditMarkerSet, cancelPlacement]);

    const handleReveal = useCallback((markerSetId, markerId = null, options = {}) => {
        if (!markerSetId) {
            return;
        }

        if (options.scrollToCanvas) {
            scrollCanvasIntoView();
        }

        window.dispatchEvent(
            new CustomEvent('markers:focus', {
                detail: {
                    markerSetId,
                    markerId,
                    select: options.select === true,
                    flash: options.flash,
                },
            })
        );

        if (options.closeDrawer) {
            setDrawerOpen(false);
        }
    }, [scrollCanvasIntoView, setDrawerOpen]);

    const handleToggleMarkerSetLock = useCallback(
        async (markerSet, nextEditableState) => {
            if (!markerSet || disabled) {
                return;
            }
            const markerSetId = markerSet.id;
            const nextLocked = !nextEditableState;
            const nextMetadata = { ...(markerSet.metadata || {}), locked: nextLocked };
            setLockSavingState((prev) => ({ ...prev, [markerSetId]: true }));

            try {
                const response = await fetch(`${baseUrl}/${markerSetId}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ metadata: nextMetadata }),
                });

                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Failed to update marker set');
                }

                setMarkerSets((prev) =>
                    prev.map((set) =>
                        (set.id ?? '').toString() === (markerSetId ?? '').toString()
                            ? { ...set, metadata: nextMetadata }
                            : set
                    )
                );
                fetchSets();
            } catch (requestError) {
                console.error(requestError);
                setError('Unable to update marker set lock state.');
            } finally {
                setLockSavingState((prev) => {
                    const next = { ...prev };
                    delete next[markerSetId];
                    return next;
                });
            }
        },
        [baseUrl, csrfToken, disabled, fetchSets]
    );

    const handleDeleteSet = useCallback(
        async (markerSetId) => {
            if (disabled) {
                return;
            }
            if (!window.confirm('Delete this marker set? This cannot be undone.')) {
                return;
            }
            try {
                const response = await fetch(`${baseUrl}/${markerSetId}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                });
                if (!response.ok) {
                    throw new Error('Failed to delete marker set');
                }
                fetchSets();
            } catch (requestError) {
                console.error(requestError);
                setError('Unable to delete marker set.');
            }
        },
        [baseUrl, csrfToken, disabled, fetchSets]
    );

    const handleCreateSet = useCallback(async () => {
        if (!createForm.name.trim()) {
            setError('Marker set name is required.');
            return;
        }
        try {
            const response = await fetch(baseUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify(createForm),
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Failed to create marker set');
            }
            setCreateDialogOpen(false);
            setCreateForm((prev) => ({ ...prev, name: '' }));
            fetchSets();
        } catch (requestError) {
            console.error(requestError);
            setError(requestError.message);
        }
    }, [baseUrl, csrfToken, createForm, fetchSets]);

    const handleCreateMarker = useCallback(async () => {
        if (!markerDialog.markerSetId) {
            return;
        }
        const labelValue = markerDialog.label ?? '';
        if (!labelValue.trim()) {
            setError('Marker label is required.');
            return;
        }
        try {
            const response = await fetch(`${baseUrl}/${markerDialog.markerSetId}/markers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    label: labelValue.trim(),
                    type: markerDialog.type,
                    sequence: markerDialog.sequence,
                        x_scaled: markerDialog.x_scaled ?? 0.5,
                        y_scaled: markerDialog.y_scaled ?? 0.5,
                    metadata: sanitizeMetadataPayload(markerDialog.metadata || {}),
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.message || 'Failed to add marker');
            }

            const createdMarker = payload?.data || null;
            if (createdMarker?.id) {
                const normalizedMarker = {
                    ...createdMarker,
                    coords: createdMarker.coords || { x_scaled: 0, y_scaled: 0 },
                };
                setMarkerSets((prev) =>
                    prev.map((set) => {
                        if (set.id !== markerDialog.markerSetId) {
                            return set;
                        }
                        return {
                            ...set,
                            markers: [...(set.markers || []), normalizedMarker],
                        };
                    })
                );
                if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
                    window.setTimeout(() => {
                        handleRequestPlacement(markerDialog.markerSetId, createdMarker.id, createdMarker.label);
                    }, 0);
                } else {
                    handleRequestPlacement(markerDialog.markerSetId, createdMarker.id, createdMarker.label);
                }
            }

            resetMarkerDialog();
            fetchSets();
        } catch (requestError) {
            console.error(requestError);
            setError(requestError.message);
        }
    }, [baseUrl, csrfToken, fetchSets, handleRequestPlacement, markerDialog, resetMarkerDialog]);

    const handleUpdateMarker = useCallback(async () => {
        if (!markerDialog.markerId || !markerDialog.markerSetId) {
            return;
        }
        const labelValue = markerDialog.label ?? '';
        if (!labelValue.trim()) {
            setError('Marker label is required.');
            return;
        }

        const payload = {
            label: labelValue.trim(),
            type: markerDialog.type,
            sequence: markerDialog.sequence,
            metadata: sanitizeMetadataPayload(markerDialog.metadata || {}),
        };

        try {
            const response = await fetch(`${baseUrl}/${markerDialog.markerSetId}/markers/${markerDialog.markerId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const responsePayload = await response.json().catch(() => ({}));
                throw new Error(responsePayload.message || 'Failed to update marker');
            }

            resetMarkerDialog();
            fetchSets();
        } catch (requestError) {
            console.error(requestError);
            setError(requestError.message);
        }
    }, [baseUrl, csrfToken, fetchSets, markerDialog, resetMarkerDialog]);

    const handleDeleteMarker = useCallback(
        async (markerSetId, markerId) => {
            if (!markerSetId || !markerId || disabled) {
                return;
            }
            if (!window.confirm('Delete this marker?')) {
                return;
            }
            try {
                const response = await fetch(`${baseUrl}/${markerSetId}/markers/${markerId}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                });
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to delete marker');
                }
                fetchSets();
            } catch (requestError) {
                console.error(requestError);
                setError(requestError.message);
            }
        },
        [baseUrl, csrfToken, disabled, fetchSets]
    );

    const handleCanvasPositionUpdate = useCallback(
        (event) => {
            const { markerId, markerSetId, coords } = event.detail || {};
            const normalizedMarkerId = markerId != null ? markerId.toString() : null;
            const normalizedMarkerSetId = markerSetId != null ? markerSetId.toString() : null;
            if (!normalizedMarkerId || !normalizedMarkerSetId || !coords) {
                return;
            }
            setMarkerSets((prev) =>
                prev.map((set) => {
                    if ((set.id ?? '').toString() !== normalizedMarkerSetId) {
                        return set;
                    }
                    return {
                        ...set,
                        markers: (set.markers || []).map((marker) =>
                            (marker.id ?? '').toString() === normalizedMarkerId
                                ? { ...marker, coords: { ...(marker.coords || {}), ...coords } }
                                : marker
                        ),
                    };
                })
            );

            if (!canEditMarkerSet(normalizedMarkerSetId)) {
                return;
            }

            setPendingSaveCount((count) => count + 1);
            fetch(`${baseUrl}/${markerSetId}/markers/${markerId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify(coords),
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('Failed to persist marker movement');
                    }
                    setLastSaveTimestamp(Date.now());
                    setSaveSnackbarState({ open: true, message: 'Marker saved' });
                })
                .catch((requestError) => {
                    console.error('Failed to persist marker movement', requestError);
                    setError('Unable to save marker movement. Please try again.');
                })
                .finally(() => {
                    setPendingSaveCount((count) => Math.max(count - 1, 0));
                });
        },
        [baseUrl, canEditMarkerSet, csrfToken]
    );

    useEffect(() => {
        window.addEventListener('markers:position:changed', handleCanvasPositionUpdate);
        return () => {
            window.removeEventListener('markers:position:changed', handleCanvasPositionUpdate);
        };
    }, [handleCanvasPositionUpdate]);

    useEffect(() => {
        const handleMarkerEditRequest = (event) => {
            const { markerId, markerSetId } = event.detail || {};
            if (!markerId || !markerSetId) {
                return;
            }
            setDrawerOpen(true);
            setPendingEditRequest({ markerId, markerSetId });
        };

        window.addEventListener('markers:edit:request', handleMarkerEditRequest);
        return () => {
            window.removeEventListener('markers:edit:request', handleMarkerEditRequest);
        };
    }, []);

    useEffect(() => {
        if (!pendingEditRequest) {
            return;
        }
        const targetSet = markerSets.find((set) => (set.id ?? '').toString() === (pendingEditRequest.markerSetId ?? '').toString());
        const targetMarker = targetSet?.markers?.find((marker) => (marker.id ?? '').toString() === (pendingEditRequest.markerId ?? '').toString());
        if (!targetSet || !targetMarker) {
            return;
        }
        handleEditMarker(targetSet, targetMarker);
        setPendingEditRequest(null);
    }, [handleEditMarker, markerSets, pendingEditRequest]);

    const canCreateMoreSets = markerSets.length < maxSets;

    const currentDialogMarkerSet = useMemo(() => {
        if (!markerDialog.markerSetId) {
            return null;
        }
        return markerSets.find((set) => set.id === markerDialog.markerSetId) || null;
    }, [markerDialog.markerSetId, markerSets]);

    const currentDialogMarker = useMemo(() => {
        if (!currentDialogMarkerSet || !markerDialog.markerId) {
            return null;
        }
        return (currentDialogMarkerSet.markers || []).find((marker) => marker.id === markerDialog.markerId) || null;
    }, [currentDialogMarkerSet, markerDialog.markerId]);

    const editingMarkerIsRequired = useMemo(() => {
        if (markerDialog.mode !== 'edit' || !currentDialogMarkerSet || !currentDialogMarker) {
            return false;
        }
        return isRequiredMarker(currentDialogMarkerSet, currentDialogMarker, templates);
    }, [currentDialogMarker, currentDialogMarkerSet, markerDialog.mode, templates]);

    const availableTypeOptions = useMemo(() => {
        if (markerDialog.mode === 'edit' && markerDialog.type === 'custom') {
            return markerTypeOptions;
        }
        return markerTypeOptions.filter((option) => option.value !== 'custom');
    }, [markerDialog.mode, markerDialog.type]);

    const isSavingMarkers = pendingSaveCount > 0;
    const lastSavedTimeLabel = lastSaveTimestamp
        ? new Date(lastSaveTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;
    const markerStatusLabel = isSavingMarkers
        ? 'Saving markers…'
        : lastSavedTimeLabel
            ? `Markers saved at ${lastSavedTimeLabel}`
            : 'No marker edits yet';
    const markerStatusColor = isSavingMarkers ? 'warning' : lastSaveTimestamp ? 'success' : 'default';

    const handleSaveSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSaveSnackbarState((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                    Markers
                </Typography>
                <Tooltip title="Open marker manager">
                    <Button size="small" variant="contained" color="info" onClick={() => setDrawerOpen(true)} startIcon={<GpsFixedIcon />}>Manage</Button>
                </Tooltip>
                {markerSets.length > 0 && markerSets.map((set) => (
                    <Tooltip title="Highlight on canvas" key={set.id}>
                        <Chip
                            label={`${set.name} (${set.markers?.length || 0})`}
                            size="small"
                            onClick={() => handleReveal(set.id, null, {
                                scrollToCanvas: true,
                                flash: { pulseCount: 2, durationMs: 260 },
                            })}
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    </Tooltip>
                ))}
            </Stack>
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 420, maxWidth: '100vw', p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>Marker Sets</Typography>
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Refresh">
                                <span>
                                    <IconButton size="small" onClick={fetchSets} disabled={loading}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Close">
                                <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    {showAutosaveHelper && (
                        <Alert
                            severity="info"
                            variant="outlined"
                            sx={{ mb: 2 }}
                            onClose={handleDismissAutosaveHelper}
                        >
                            Markers save automatically after each drag or edit. Use the Editable slider on every
                            set to lock coordinates once you are finished adjusting them.
                        </Alert>
                    )}

                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                          <Chip
                              size="small"
                              label={markerStatusLabel}
                              color={markerStatusColor}
                              variant={markerStatusColor === 'default' ? 'outlined' : 'filled'}
                          />
                      </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {disabled && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Editing is locked for this flux. You can still preview markers on the canvas.
                        </Alert>
                    )}

                    {placementTarget && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                            action={
                                <Button color="inherit" size="small" onClick={cancelPlacement}>
                                    Cancel
                                </Button>
                            }
                        >
                            Click on the canvas to place <strong>{placementTarget.label}</strong>. Press Esc to cancel.
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!canCreateMoreSets || disabled}
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            Add Marker Set
                        </Button>
                    </Stack>
                    {!canCreateMoreSets && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Max {maxSets} marker sets reached for this flux.
                        </Alert>
                    )}

                    {loading ? (
                        <Stack alignItems="center" sx={{ mt: 4 }}>
                            <CircularProgress size={32} />
                        </Stack>
                    ) : (
                        markerSets.length ? (
                              markerSets.map((markerSet) => {
                                  const remainingDefinitions = computeAvailableDefinitions(markerSet);
                                  const setLocked = isMarkerSetLocked(markerSet);
                                  const setEditable = !setLocked && !disabled;
                                  const lockSaving = Boolean(lockSavingState[markerSet.id]);
                                  const usageInfo = markerUsage[(markerSet?.id ?? '').toString()] || null;
                                  const setInUse = Boolean(usageInfo?.count);
                                  const usageTooltip = setInUse
                                      ? `Used by ${usageInfo.count} analyzer${usageInfo.count === 1 ? '' : 's'} across ${usageInfo.zones.length} zone${usageInfo.zones.length === 1 ? '' : 's'}.`
                                      : 'Delete marker set';
                                return (
                                <Accordion key={markerSet.id} sx={{ mb: 1 }} defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack spacing={0.5} sx={{ width: '100%' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography fontWeight={600}>{markerSet.name}</Typography>
                                                      <Stack direction="row" spacing={1} alignItems="center">
                                                          <FormControlLabel
                                                              control={(
                                                                  <Switch
                                                                      size="small"
                                                                      checked={!setLocked}
                                                                      onChange={(event) =>
                                                                          handleToggleMarkerSetLock(markerSet, event.target.checked)
                                                                      }
                                                                      disabled={disabled || lockSaving}
                                                                  />
                                                              )}
                                                              label={setLocked ? 'Locked' : 'Editable'}
                                                              sx={{ mr: 0 }}
                                                          />
                                                    <Tooltip title="Reveal on canvas">
                                                        <IconButton size="small" onClick={() => handleReveal(markerSet.id, null, { closeDrawer: true })}>
                                                            <CenterFocusStrongIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={usageTooltip}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteSet(markerSet.id)}
                                                                disabled={disabled || setInUse}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip size="small" label={markerSet.color_key} />
                                                <Chip size="small" label={markerSet.type} variant="outlined" />
                                                {setInUse && (
                                                    <Tooltip
                                                        title={usageInfo?.zones?.map((entry) => `${entry.zoneName} • ${entry.analyzerName}`).join('\n') || 'In use'}
                                                    >
                                                        <Chip size="small" color="warning" label={`In use (${usageInfo.count})`} />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {markerSet.legend && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {markerSet.legend}
                                            </Typography>
                                        )}
                                        <Divider sx={{ mb: 1 }} />
                                        <List dense>
                                            {markerSet.markers.map((marker) => {
                                                const required = isRequiredMarker(markerSet, marker, templates);
                                                const colors = resolveMarkerColors(
                                                    colorSchemes,
                                                    markerSet.color_key,
                                                    marker,
                                                    templates[markerSet.type]
                                                );
                                                return (
                                                    <ListItem
                                                        key={marker.id}
                                                        sx={{ borderLeft: `4px solid ${colors.fill}`, mb: 0.5, cursor: 'pointer' }}
                                                        alignItems="flex-start"
                                                        onClick={() => handleReveal(markerSet.id, marker.id, { closeDrawer: true, scrollToCanvas: true })}
                                                        onDoubleClick={
                                                            setEditable
                                                                ? (event) => {
                                                                      event.stopPropagation();
                                                                      handleRequestPlacement(markerSet.id, marker.id, marker.label);
                                                                  }
                                                                : undefined
                                                        }
                                                        secondaryAction={
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <Tooltip title="Highlight on canvas">
                                                                    <IconButton size="small" onClick={(event) => { event.stopPropagation(); handleReveal(markerSet.id, marker.id, { closeDrawer: true, scrollToCanvas: true, flash: { pulseCount: 3, durationMs: 280 } }); }}>
                                                                        <MyLocationIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Move on canvas">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            disabled={!setEditable}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleMoveMarker(markerSet.id, marker.id);
                                                                            }}
                                                                        >
                                                                            <PlaceOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <Tooltip title="Edit marker details">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            disabled={!setEditable}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleEditMarker(markerSet, marker);
                                                                            }}
                                                                        >
                                                                            <EditOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                {!required && (
                                                                    <Tooltip title="Delete marker">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                disabled={!setEditable}
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    handleDeleteMarker(markerSet.id, marker.id);
                                                                                }}
                                                                            >
                                                                                <DeleteOutlineIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </Stack>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Typography fontWeight={600}>{marker.label}</Typography>
                                                                    {required && <Chip size="small" label="Required" color="warning" />}
                                                                </Stack>
                                                            }
                                                            secondary={
                                                                <Stack spacing={0.5}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {marker.type} • x: {Number(marker.coords?.x_scaled ?? 0).toFixed(3)}, y: {Number(marker.coords?.y_scaled ?? 0).toFixed(3)}
                                                                    </Typography>
                                                                    {(marker.metadata?.real_dx != null || marker.metadata?.real_dy != null || marker.metadata?.height != null) && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Δx: {marker.metadata?.real_dx ?? '-'} | Δy: {marker.metadata?.real_dy ?? '-'} | h: {marker.metadata?.height ?? '-'}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            }
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                        {remainingDefinitions.length === 0 && !disabled && (
                                            <Alert severity="info" sx={{ mb: 1 }}>
                                                All calibration points from the selected template are already active.
                                            </Alert>
                                        )}
                                          <Button
                                              fullWidth
                                              variant="outlined"
                                              size="small"
                                              disabled={!setEditable || remainingDefinitions.length === 0}
                                            onClick={() => handleOpenMarkerDialog(markerSet)}
                                            startIcon={<AddIcon />}
                                        >
                                            Add Marker
                                        </Button>
                                    </AccordionDetails>
                                </Accordion>
                            );
                            })
                        ) : (
                            <Alert severity="info">No marker sets yet. Create one to begin mapping calibration points.</Alert>
                        )
                    )}
                </Box>
            </Drawer>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Create Marker Set</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Name"
                            value={createForm.name}
                            onChange={(event) => setCreateForm({ ...createForm, name: event.target.value })}
                            autoFocus
                            required
                        />
                        <TextField
                            select
                            label="Color scheme"
                            value={createForm.color_key}
                            onChange={(event) => setCreateForm({ ...createForm, color_key: event.target.value })}
                        >
                            {Object.keys(colorSchemes).map((key) => (
                                <MenuItem key={key} value={key}>{colorSchemes[key]?.label || key}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Template"
                            value={createForm.type}
                            onChange={(event) => setCreateForm({ ...createForm, type: event.target.value })}
                        >
                            {Object.keys(templates).map((key) => (
                                <MenuItem key={key} value={key}>{templates[key]?.label || key}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Legend (optional)"
                            multiline
                            minRows={2}
                            value={createForm.legend}
                            onChange={(event) => setCreateForm({ ...createForm, legend: event.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSet} disabled={!createForm.name.trim()}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={markerDialog.open} onClose={handleMarkerDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>{markerDialog.mode === 'edit' ? 'Edit Marker' : 'Add Marker'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {markerDialog.mode === 'create' && markerDialog.definitions.length > 0 && (
                            <>
                                <Alert severity="info">
                                    Select the calibration point you want to place. Required points disappear after placement, while repeatable points stay available.
                                </Alert>
                                <TextField
                                    select
                                    label="Calibration point"
                                    value={markerDialog.label}
                                    onChange={(event) => {
                                        const definition = markerDialog.definitions.find((entry) => entry.label === event.target.value) || {};
                                        setMarkerDialog((prev) => ({
                                            ...prev,
                                            label: event.target.value,
                                            type: definition.type || prev.type,
                                            sequence: definition.sequence ?? prev.sequence,
                                        }));
                                    }}
                                >
                                    {markerDialog.definitions.map((definition) => (
                                        <MenuItem key={definition.label} value={definition.label}>
                                            {definition.optionLabel || definition.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </>
                        )}
                        <TextField
                            label="Label"
                            value={markerDialog.label}
                            onChange={(event) => setMarkerDialog((prev) => ({ ...prev, label: event.target.value }))}
                            disabled={markerDialog.mode === 'create' || editingMarkerIsRequired}
                        />
                        <TextField
                            select
                            label="Type"
                            value={markerDialog.type}
                            onChange={(event) => setMarkerDialog((prev) => ({ ...prev, type: event.target.value }))}
                            disabled={markerDialog.mode === 'create' || editingMarkerIsRequired}
                        >
                            {availableTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="number"
                            label="Sequence"
                            value={markerDialog.sequence}
                            onChange={(event) => setMarkerDialog((prev) => ({ ...prev, sequence: Number(event.target.value) }))}
                            disabled={markerDialog.mode === 'create' || editingMarkerIsRequired}
                        />
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2">Real-world measurements</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                type="number"
                                label="Δx (meters)"
                                value={markerDialog.metadata.real_dx}
                                onChange={(event) => setMarkerDialog((prev) => ({
                                    ...prev,
                                    metadata: { ...prev.metadata, real_dx: event.target.value },
                                }))}
                                inputProps={{ step: '0.01' }}
                            />
                            <TextField
                                type="number"
                                label="Δy (meters)"
                                value={markerDialog.metadata.real_dy}
                                onChange={(event) => setMarkerDialog((prev) => ({
                                    ...prev,
                                    metadata: { ...prev.metadata, real_dy: event.target.value },
                                }))}
                                inputProps={{ step: '0.01' }}
                            />
                        </Stack>
                        <TextField
                            type="number"
                            label="[optional and depends on model] Height (pixels) "
                            value={markerDialog.metadata.height}
                            onChange={(event) => setMarkerDialog((prev) => ({
                                ...prev,
                                metadata: { ...prev.metadata, height: event.target.value },
                            }))}
                            inputProps={{ step: '0.01' }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMarkerDialogClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={markerDialog.mode === 'edit' ? handleUpdateMarker : handleCreateMarker}
                        disabled={!markerDialog.markerSetId || !markerDialog.label}
                    >
                        {markerDialog.mode === 'edit' ? 'Save Changes' : 'Add Marker'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={saveSnackbarState.open}
                autoHideDuration={2500}
                onClose={handleSaveSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSaveSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {saveSnackbarState.message || 'Markers saved'}
                </Alert>
            </Snackbar>
        </Box>
    );
};

/**
 * Picks a color swatch for the marker based on the configured scheme and template variants.
 */
const resolveMarkerColors = (colorSchemes, colorKey, marker, template) => {
    const scheme = colorSchemes[colorKey]?.swatches || {};
    const outline = scheme.outline || '#1C1E31';
    const variant = resolveVariant(marker.label);
    const swatch = scheme[marker.type];

    if (typeof swatch === 'string') {
        return { fill: swatch, outline };
    }

    if (swatch && typeof swatch === 'object') {
        if (variant && swatch[variant]) {
            return { fill: swatch[variant], outline };
        }
        const fallback = swatch.a || swatch.b || Object.values(swatch)[0];
        if (fallback) {
            return { fill: fallback, outline };
        }
    }

    if (template && Array.isArray(template.required_markers)) {
        const requiredMatch = template.required_markers.find((entry) => entry.label === marker.label);
        if (requiredMatch?.color) {
            return { fill: requiredMatch.color, outline };
        }
    }

    return { fill: '#FF7043', outline };
};

/**
 * Determines whether the provided marker label is protected by the active template definition.
 */
const isRequiredMarker = (markerSet, marker, templates) => {
    const template = templates[markerSet.type];
    if (!template || !Array.isArray(template.required_markers)) {
        return marker.type === 'start' || marker.type === 'end';
    }
    return template.required_markers.some((entry) => entry.label === marker.label);
};

MarkersManager.propTypes = {
    bootstrap: PropTypes.object,
};

export default MarkersManager;
